/**
 * @file history-mixin.ts
 *
 * Exposes the undo/redo history API as as-app methods, and turns history off
 * while the host carries the `disable-undo-history` attribute.
 * Wraps undoHistory.ts so app code never imports the module directly.
 *
 * Exposes: clearHistory(), startHistoryBatch(), finishHistoryBatch(),
 *          cancelHistoryBatch(), isHistoryBatchActive()
 */
import {
  resetHistory,
  setHistoryEnabled,
  startHistoryBatch as _start,
  finishHistoryBatch as _finish,
  cancelHistoryBatch as _cancel,
  isHistoryBatchActive as _isActive,
  type HistoryAction,
} from '../../../features/history/undoHistory.ts';
import { type Constructor } from './mixin-types-utils.ts';

/** Surface added by HistoryMixin. */
export interface HistoryMixinMembers {
  clearHistory(): void;
  startHistoryBatch(label?: string, tags?: string[]): void;
  finishHistoryBatch(): HistoryAction[];
  cancelHistoryBatch(): void;
  isHistoryBatchActive(): boolean;
}

export const HistoryMixin = <T extends Constructor<HTMLElement>>(Base: T) =>
  class HistoryMixin extends Base {

    attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
      super.attributeChangedCallback?.(name, oldValue, newValue);
      if (name === 'disable-undo-history') setHistoryEnabled(newValue === null);
    }

    clearHistory(): void {
      resetHistory();
    }

    /**
     * Open a history batch. All history pushes until finishHistoryBatch() are
     * buffered and collapsed into a single undo entry on close.
     * Nested calls are ignored — the outermost caller owns the batch.
     *
     * @param label - Label shown in the history stack UI.
     * @param tags  - String tags included in lifecycle events and the merged
     *                action, useful for identifying batches in listeners.
     *
     * Dispatches: `as-history-batch-start` with `{ label, tags }`.
     */
    startHistoryBatch(label?: string, tags?: string[]): void {
      _start(label, tags);
    }

    /**
     * Close the history batch and push the merged action to the stack.
     * Returns any entries dropped from the stack (for token cleanup).
     *
     * Dispatches: `as-history-batch-end` with `{ label, tags, action }`.
     */
    finishHistoryBatch(): HistoryAction[] {
      return _finish();
    }

    /**
     * Discard the active history batch without pushing anything.
     * Dispatches: `as-history-batch-cancel` with `{ label, tags }`.
     */
    cancelHistoryBatch(): void {
      _cancel();
    }

    /** True while a history batch is open. */
    isHistoryBatchActive(): boolean {
      return _isActive();
    }
  };
